import { useId } from "react";
import { X } from "lucide-react";
import Modal from "../elements/Modal";
import { classNames } from "../../helpers/classNames";

/**
 * The redesign's pop-up (docs/design/HANDOFF.md, "Pop-ups"): a circle icon,
 * a serif heading, one plain sentence, then buttons. Mobile stacks the
 * buttons full width; tablet and desktop put them side by side; desktop adds
 * a close X. Built on the existing `Modal` (focus trap, backdrop, animation).
 *
 * `tone`    "rose" (default, recovery and neutral), "red" (error, destructive),
 *           "green" (success)
 * `actions` buttons in order of importance; the first is drawn first on
 *           mobile and on the right on wider screens.
 * `alert`   true for error and destructive pop-ups (role="alertdialog").
 */
const TONES = {
  rose: "bg-md-pink/10 text-md-pink-dark",
  red: "bg-red-50 text-red-700",
  green: "bg-green-50 text-green-800",
  amber: "bg-amber-50 text-amber-700",
  // Ordered books: the same warm gray as their status pill.
  gray: "bg-[#EDE8E2] text-[#4A4446]",
};

const Popup = ({ open, onClose, icon = null, tone = "rose", title, children, actions = [], alert = false, size = "md", dismissible = true }) => {
  const titleId = useId();
  const bodyId = useId();
  return (
    <Modal
      isOpen={open}
      setIsOpen={(next) => {
        if (!next && dismissible) onClose?.();
      }}
      header={false}
      isClose={false}
      isOutsideClose={dismissible}
      size={size}
      modalDialogueStyle="!rounded-[22px] md:!rounded-3xl"
      bodyClasses="!px-5 !pt-7 !pb-5 md:!px-8 md:!pt-9 md:!pb-7"
    >
      <div
        role={alert ? "alertdialog" : "dialog"}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={children ? bodyId : undefined}
        className="flex flex-col items-center gap-4 text-center"
      >
        {dismissible && onClose ? (
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="absolute right-2.5 top-2.5 hidden h-11 w-11 items-center justify-center rounded-md text-md-muted lg:flex focus:outline-none focus-visible:ring-2 focus-visible:ring-md-pink"
          >
            <X className="h-6 w-6" aria-hidden="true" />
          </button>
        ) : null}
        {icon ? (
          <span aria-hidden="true" className={classNames("flex h-14 w-14 items-center justify-center rounded-full", TONES[tone] || TONES.rose)}>
            {icon}
          </span>
        ) : null}
        <div className="flex flex-col gap-1.5">
          <h2 id={titleId} className="m-0 font-heading text-[26px] font-normal leading-tight text-md-dark md:text-[28px]">
            {title}
          </h2>
          {children ? (
            <div id={bodyId} className="text-[15px] leading-snug text-md-muted md:text-base">
              {children}
            </div>
          ) : null}
        </div>
        {actions.length ? (
          <div className="mt-1 flex w-full flex-col gap-2.5 md:flex-row-reverse md:justify-center [&>*]:md:flex-1">{actions}</div>
        ) : null}
      </div>
    </Modal>
  );
};

export default Popup;
